import { useState, useEffect } from "preact/hooks";
import { newId, hashPin, revealPin, type User, type UserId, type VenueId, type Role } from "@orderman/shared";
import { adminEvents, configFromFeed, adminDirty } from "../../state/adminData.js";
import { settings } from "../../state/config.js";
import { writeConfigSnapshot } from "../../state/store.js";

// Personal mobil verwalten (Name/Rolle/PIN/aktiv). Schreibt users-config.snapshot über den
// Geräte-Token — dieselbe Logik wie der Portal-PersonalEditor, nur Karten statt Tabelle.

type EditUser = User & { newPin: string };
const ROLES: { key: Role; label: string }[] = [
  { key: "waiter", label: "Kellner" },
  { key: "kitchen", label: "Küche" },
  { key: "admin", label: "Admin" },
];

function PersonalEditor({ initUsers, initVersion }: { initUsers: User[]; initVersion: number }) {
  const venueId = settings.value.venueId as VenueId;
  const [rows, setRows] = useState<EditUser[]>(() => initUsers.map((u) => ({ ...u, newPin: "" })));
  const [version, setVersion] = useState(initVersion);
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState("");

  // Frischer Snapshot beim Mount → keine Alt-Warnung übernehmen.
  useEffect(() => {
    adminDirty.value = false;
  }, []);
  const touch = () => (adminDirty.value = true);

  const patch = (i: number, p: Partial<EditUser>) => {
    touch();
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...p } : r)));
  };
  const add = () => {
    touch();
    setRows((rs) => [
      ...rs,
      { id: newId() as UserId, venueId, name: "", pinHash: "", role: "waiter", isActive: true, newPin: "" },
    ]);
  };
  const del = (i: number) => {
    touch();
    setRows((rs) => rs.filter((_, j) => j !== i));
  };
  const toggleReveal = (u: EditUser) =>
    setRevealed((r) => {
      if (r[u.id] !== undefined) {
        const { [u.id]: _drop, ...rest } = r;
        return rest;
      }
      return { ...r, [u.id]: revealPin(u.pinHash) ?? "?" };
    });

  const save = () => {
    for (const r of rows) {
      if (!r.name.trim()) return setMsg("Jede Person braucht einen Namen.");
      if (!r.pinHash && !r.newPin.trim()) return setMsg(`PIN für „${r.name || "neu"}" fehlt.`);
    }
    const users: User[] = rows.map(({ newPin, ...u }) => ({
      ...u,
      name: u.name.trim(),
      pinHash: newPin.trim() ? hashPin(newPin.trim()) : u.pinHash,
    }));
    const next = version + 1;
    writeConfigSnapshot("users", users, next);
    setVersion(next);
    setRows(users.map((u) => ({ ...u, newPin: "" })));
    adminDirty.value = false;
    setMsg("✓ Gespeichert – wird an alle Geräte verteilt.");
    setTimeout(() => setMsg(""), 2500);
  };

  return (
    <div class="admin-scroll">
      {rows.map((r, i) => (
        <div key={r.id} class="pers-card">
          <div class="pers-row">
            <input
              class="pers-name"
              placeholder="Name"
              value={r.name}
              onInput={(e) => patch(i, { name: e.currentTarget.value })}
            />
            <button class="pers-del" onClick={() => del(i)} aria-label="Entfernen">
              ✕
            </button>
          </div>

          <div class="pers-roles">
            {ROLES.map((ro) => (
              <button
                key={ro.key}
                class={r.role === ro.key ? "seg-btn on" : "seg-btn"}
                onClick={() => patch(i, { role: ro.key })}
              >
                {ro.label}
              </button>
            ))}
          </div>

          <div class="pers-row2">
            <div class="pin-cell">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder={r.pinHash ? "PIN ändern…" : "PIN"}
                value={r.newPin}
                onInput={(e) => patch(i, { newPin: e.currentTarget.value })}
              />
              {r.pinHash && !r.newPin && (
                <button class="pin-eye" onClick={() => toggleReveal(r)} aria-label="PIN anzeigen">
                  {revealed[r.id] !== undefined ? "🙈" : "👁"}
                </button>
              )}
              {revealed[r.id] !== undefined && <span class="pin-revealed">{revealed[r.id]}</span>}
            </div>
            <button
              class={`av-toggle ${r.isActive ? "on" : "off"}`}
              onClick={() => patch(i, { isActive: !r.isActive })}
            >
              {r.isActive ? "Aktiv" : "Inaktiv"}
            </button>
          </div>
        </div>
      ))}

      <button class="pers-add" onClick={add}>
        + Person
      </button>

      {msg && <div class="admin-flash">{msg}</div>}

      <div class="av-savebar">
        <button class="btn-primary" onClick={save}>
          Speichern
        </button>
      </div>
    </div>
  );
}

export function PersonalView() {
  if (adminEvents.value === null) return <div class="admin-empty">Lade Personal…</div>;
  const cfg = configFromFeed(adminEvents.value);
  return <PersonalEditor initUsers={cfg.users} initVersion={cfg.usersVersion} />;
}
